import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BillingEventStatus,
  BillingPlan,
  CancelSubscription,
  ChairReconciliation,
  ChangePlan,
  CommsOveragePreview,
  CreateSubscription,
  DunningStatus,
  Invoice,
  InvoiceBreakdown,
  InvoiceStatus,
  SetPaymentMethod,
  Subscription,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS10 — SaaS billing & subscriptions: Salonomy's OWN revenue (brief 10 §3/§4; ADR-0010 + ADR-0011).
 * TanStack Query over the generated client, mirroring data/services.ts / data/vouchers.ts. This is the
 * console proof of the FOUNDATION promise — "No contracts, ever. Monthly rolling, in-app one-click
 * cancel, itemised invoices." — so the data layer is held to that standard:
 *
 * HONESTY (non-negotiable): every amount comes from the API. The hooks DISPLAY the server's figures —
 * the subscription's items, each invoice's totals, the per-invoice /breakdown (base + chairs × rate WITH
 * the staff list behind the count + comms overage drilling to the message audit + a single derived VAT
 * line) — and never recompute money, VAT, or proration on the client. Plan prices read here are a
 * PROPOSAL (the seed numbers remain unconfirmed until business sign-off — ADR-0010 §2).
 *
 * LIVE READS: financial reads use staleTime 0 — money is re-fetched, never shown stale — and NONE of
 * these keys are in PERSISTED_PREFIXES (lib/query.ts), so a balance is NEVER served from the offline
 * IndexedDB replica. The lifecycle (trial / active / scheduled-cancel) and the dunning/read-only ladder
 * are read from the API too; the UI shows the state honestly and invents nothing.
 *
 * ONE-CLICK CANCEL (ADR-0011 §4): the cancel mutation takes the single `confirm: true` and is effective
 * end-of-period (`cancelAtPeriodEnd`), never immediate; it returns the GDPR `exportUrl` to offer on the
 * way out. NO retention flow, NO discount-to-stay — there is nothing to add here, by design.
 * Reactivation (clearing the scheduled cancel) is the equally-trivial inverse.
 */

export const INVOICES_PAGE = 50;
export const EVENTS_PAGE = 50;

export interface InvoicePage {
  data: Invoice[];
  pagination: { limit: number; offset: number; total: number };
}

/** One ingested Stripe Billing webhook event (audit/debug); inline on the API, no named entity. */
export interface BillingEvent {
  id: string;
  stripeEventId: string;
  type: string;
  status: BillingEventStatus;
  receivedAt: string;
}

export interface BillingEventPage {
  data: BillingEvent[];
  pagination: { limit: number; offset: number; total: number };
}

/** The cancel response: the now-scheduled subscription + the GDPR book-export path offered on exit. */
export interface CancelResult {
  subscription: Subscription;
  /** The GDPR book-export path the UI links on the way out (data is never deleted on cancel). */
  exportUrl: string;
}

// ── Reads (financial; LIVE — staleTime 0, never persisted) ──────────────────────────

/**
 * This salon's subscription: the lifecycle state (trialing / active / past_due / canceled), whether a
 * cancel is scheduled (`cancelAtPeriodEnd`), the trial/period boundaries, and the per-line items (base +
 * per_chair quantity + comms_overage) — every figure server-derived. Owner-only on the API.
 */
export function useSubscription(enabled = true) {
  return useQuery({
    queryKey: qk.billingSubscription,
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<Subscription> => unwrap(await api.GET('/v1/billing/subscription')),
  });
}

/**
 * The available billing plans as CONFIG (the same rows the biller charges from — ADR-0010 §2). Prices
 * here are a PROPOSAL; surface them as such. Config, so not gated as tightly as the money reads.
 */
export function usePlans(enabled = true) {
  return useQuery({
    queryKey: qk.billingPlans,
    enabled,
    queryFn: async (): Promise<BillingPlan[]> => unwrap(await api.GET('/v1/billing/plans')),
  });
}

/**
 * This salon's position on the PUBLISHED dunning ladder (ADR-0011 §3): the named stage, the derived
 * write-gate state (none / dunning / read_only), days elapsed / until read-only, and the published
 * retry/grace/read-only schedule behind it — so the read-only state is never a surprise. Always
 * reachable (emergency-access READ route, exempt from the write-gate); read live.
 */
export function useDunning(enabled = true) {
  return useQuery({
    queryKey: qk.billingDunning,
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<DunningStatus> => unwrap(await api.GET('/v1/billing/dunning')),
  });
}

/** This salon's itemised invoices (owner-only), newest first; optional status filter, paged. */
export function useInvoices(status?: InvoiceStatus, offset = 0) {
  return useQuery({
    queryKey: qk.billingInvoices(status ?? null, offset),
    staleTime: 0,
    queryFn: async (): Promise<InvoicePage> =>
      unwrap(
        await api.GET('/v1/billing/invoices', {
          params: { query: { status, limit: INVOICES_PAGE, offset } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** One invoice with its itemised lines (the totals row of the "why is this amount" view). */
export function useInvoice(id: string | null) {
  return useQuery({
    queryKey: id ? qk.billingInvoice(id) : ['billing', 'invoice', 'none'],
    enabled: Boolean(id),
    staleTime: 0,
    queryFn: async (): Promise<Invoice> =>
      unwrap(await api.GET('/v1/billing/invoices/{id}', { params: { path: { id: id! } } })),
  });
}

/**
 * The full "why is this invoice this amount" breakdown (Overview §6.7; brief 10 §3): base plan + chairs ×
 * per-chair rate WITH the staff list behind the count + comms overage drilling to the message audit + a
 * single derived VAT line — every charge line carrying its `provenance` back to logged platform usage.
 * Server-assembled; the UI drills, it never re-derives. Gated on an invoice id; read live.
 */
export function useInvoiceBreakdown(id: string | null) {
  return useQuery({
    queryKey: id ? qk.billingInvoiceBreakdown(id) : ['billing', 'invoice', 'none', 'breakdown'],
    enabled: Boolean(id),
    staleTime: 0,
    queryFn: async (): Promise<InvoiceBreakdown> =>
      unwrap(
        await api.GET('/v1/billing/invoices/{id}/breakdown', { params: { path: { id: id! } } }),
      ),
  });
}

/**
 * The current period's comms-overage receipts (auto_top_up salons only): the metered/included/overage
 * units per channel, the published rate, the amount, and the audit message ids each overage unit drills
 * to. A pause/ask salon returns `billable: false` and zero everywhere — it is never charged for comms.
 * The window defaults to the subscription period server-side; pass an explicit range to override.
 */
export function useOverage(
  range: { periodStart?: string; periodEnd?: string } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: qk.billingOverage(range.periodStart ?? null, range.periodEnd ?? null),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<CommsOveragePreview> =>
      unwrap(
        await api.GET('/v1/billing/overage', {
          params: { query: { periodStart: range.periodStart, periodEnd: range.periodEnd } },
        }),
      ),
  });
}

/** Recently ingested Stripe Billing webhook events for this salon (audit/debug), newest first, paged. */
export function useBillingEvents(offset = 0, enabled = true) {
  return useQuery({
    queryKey: qk.billingEvents(offset),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<BillingEventPage> =>
      unwrap(
        await api.GET('/v1/billing/events', {
          params: { query: { limit: EVENTS_PAGE, offset } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

// ── Mutations (any change reconciles the subscription + dunning state) ───────────────

/**
 * Subscribe to a plan — starts the plan-configured trial with card capture DECOUPLED (no immediate
 * charge — ADR-0011 §1). One-trial-per-salon is enforced server-side: a re-subscribe after a consumed
 * trial resumes PAID, it does not re-trial. The caller mints `idempotencyKey` once per intent.
 */
export function useCreateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateSubscription): Promise<Subscription> =>
      unwrap(await api.POST('/v1/billing/subscription', { body: body as never })),
    onSuccess: (subscription) => {
      qc.setQueryData(qk.billingSubscription, subscription);
      invalidateBilling(qc);
    },
  });
}

/**
 * Attach / replace the salon's default payment method on the platform Customer (no charge). The
 * PaymentMethod id is captured client-side (Stripe Elements) and POSTed here. Returns the server's
 * `hasPaymentMethod` flag (the trial "add a card" / dunning "update card" CTA reads this).
 */
export function useSetPaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetPaymentMethod): Promise<{ hasPaymentMethod: boolean }> =>
      unwrap(await api.PUT('/v1/billing/payment-method', { body: body as never })),
    // A new card can clear dunning once Stripe retries; re-read the lifecycle/dunning state.
    onSuccess: () => invalidateBilling(qc),
  });
}

/** Change plan — takes effect at the NEXT cycle (ADR-0011 §2), never mid-cycle. */
export function useChangePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ChangePlan): Promise<Subscription> =>
      unwrap(await api.PUT('/v1/billing/plan', { body: body as never })),
    onSuccess: (subscription) => {
      qc.setQueryData(qk.billingSubscription, subscription);
      invalidateBilling(qc);
    },
  });
}

/**
 * Recompute billable chairs from active staff and set the per-chair item quantity (Overview §6.7 — the
 * count WITH the staff behind it). Mid-cycle additions prorate; a removal never bills more (the rule is
 * published). Returns the reconciliation (the count, the rate, the named staff, and whether it `applied`).
 */
export function useReconcileChairs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ChairReconciliation> =>
      unwrap(await api.POST('/v1/billing/reconcile-chairs')),
    // A reconcile can change the per_chair item quantity — re-read the subscription.
    onSuccess: () => invalidateBilling(qc),
  });
}

/**
 * One-click cancel (ADR-0011 §4): ONE confirmation, effective END of the paid period
 * (`cancelAtPeriodEnd`), never immediate. NO retention flow, NO discount-to-stay — there is nothing else
 * to pass. Returns the now-scheduled subscription + the GDPR `exportUrl` to offer on the way out; data is
 * never deleted. `body.confirm` is the single confirmation the marketing copy promises.
 */
export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CancelSubscription): Promise<CancelResult> =>
      unwrap(await api.POST('/v1/billing/cancel', { body: body as never })),
    onSuccess: (result) => {
      qc.setQueryData(qk.billingSubscription, result.subscription);
      invalidateBilling(qc);
    },
  });
}

/** Reactivate before period end — clears the scheduled cancel and resumes paid (the trivial inverse). */
export function useReactivateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<Subscription> => unwrap(await api.POST('/v1/billing/reactivate')),
    onSuccess: (subscription) => {
      qc.setQueryData(qk.billingSubscription, subscription);
      invalidateBilling(qc);
    },
  });
}

// ── Invalidation helper ──────────────────────────────────────────────────────────────

/**
 * Any subscribe/change/reconcile/cancel/reactivate (or a fresh card) can shift the lifecycle state, the
 * per-chair items, the dunning ladder, and the invoice set — re-read the whole billing surface (the
 * `['billing']` prefix covers every key in the qk registry).
 */
function invalidateBilling(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['billing'] });
}
